import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useParams } from "react-router";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";

import Loader from "../../components/Loader";
import ImageCard from "../../components/images/ImageCard";
import ZoomDialog from "../../components/images/ZoomDialog";
import CompareDialog from "../../components/images/CompareDialog";
import EcrfBlocks from "../../components/Trials/EcrfBlocks";
import useGetGalleryPhotos from "../../hooks/useGetGalleryPhotos";
import useGetTrialPhotoTypes from "../../hooks/useGetTrialPhotoTypes";
import { INV_RELATED_ROLES, USER_ROLES } from "../../utils/constants";

const GalleryView = () => {
  const { _id_ } = useParams();
  const userData = JSON.parse(localStorage.getItem("user_data"));
  const role = userData?.role;

  const { trialsList, trialFilter, setActive, setBreadcrumbTitle, isTrialLocked } =
    useOutletContext();
  setActive("photos");
  setBreadcrumbTitle("Photos - Gallery View");

  // Compare dialog
  const [compareOpen, setCompareOpen] = useState(false);
  const [selectedCompareImage, setSelectedCompareImage] = useState(null);

  // Zoom dialog
  const [zoomImage, setZoomImage] = useState(null);

  // Score eCRF dialog
  const [scoreContext, setScoreContext] = useState(null);

  const canScore =
    !isTrialLocked && (INV_RELATED_ROLES.includes(role) || role === USER_ROLES.REVIEWER);

  const [filterData, setFilterData] = useState({
    photo_types: "",
    area: "",
    trial_area: trialFilter ?? "",
    selected_user: "",
  });

  const handleOnImageClick = (img) => {
    setCompareOpen(true);
    setSelectedCompareImage(img);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const { data: patientData, isLoading } = useGetGalleryPhotos({
    patientId: _id_,
    photoType: filterData.photo_types,
    photoArea: filterData.area,
    selectedUser: filterData.selected_user,
    trialArea: filterData.trial_area,
  });

  const { data: trialPhotoTypes = [] } = useGetTrialPhotoTypes({
    trialId: filterData.trial_area,
    patientId: _id_,
  });

  return (
    <>
      <Paper elevation={1} sx={{ p: 2 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Photos – Gallery View</Typography>

          <Stack direction="row" spacing={1}>
            <Button component={Link} to={`/patient/${_id_}/photos`} variant="outlined" size="small">
              Session View
            </Button>
            <Button
              component={Link}
              to={`/patient/${_id_}/gallery`}
              variant="contained"
              size="small"
            >
              Gallery View
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Trial</InputLabel>
            <Select
              label="Trial"
              name="trial_area"
              value={filterData.trial_area}
              onChange={handleChange}
            >
              <MenuItem value="">All Trial Areas</MenuItem>
              <MenuItem value={0}>Appiell Wrinkles</MenuItem>
              {trialsList.map((trial) => (
                <MenuItem key={trial.id} value={trial.id}>
                  {trial.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Photo Type</InputLabel>
            <Select
              label="Photo Type"
              name="photo_types"
              value={filterData.photo_types}
              onChange={handleChange}
            >
              <MenuItem value="">All Types</MenuItem>
              {trialPhotoTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name_str}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Loader */}
        {isLoading ? (
          <Stack alignItems="center" py={4}>
            <Loader />
          </Stack>
        ) : (
          <>
            {/* Gallery */}
            {patientData &&
              patientData?.map((row) => (
                <Box key={row.id} mb={4}>
                  {row.photo.length > 0 && (
                    <>
                      <Stack
                        direction="row"
                        spacing={3}
                        mb={2}
                        sx={{
                          justifyContent: "flex-start",
                          alignItems: "flex-end",
                        }}
                      >
                        <Typography variant="h6">Visit Name: {row.visit_name}</Typography>

                        {row.max_scores?.repose_score !== null &&
                          row.max_scores?.repose_score !== undefined && (
                            <Typography variant="body2" color="text.secondary">
                              Repose Score: {row.max_scores.repose_score}
                            </Typography>
                          )}

                        {row.max_scores?.active_score !== null &&
                          row.max_scores?.active_score !== undefined && (
                            <Typography variant="body2" color="text.secondary">
                              Active Score: {row.max_scores.active_score}
                            </Typography>
                          )}
                      </Stack>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                          gap: 2,
                        }}
                      >
                        {row.photo.map((photo) => (
                          <ImageCard
                            key={photo.id}
                            photo={photo}
                            onCompare={() => handleOnImageClick(photo.image)}
                            onZoom={() => setZoomImage(photo.image)}
                            showConfidence={role === "Super Admin"}
                            showScoreButton={Boolean(row.score_ecrf_name)}
                            canScore={canScore}
                            onScore={() =>
                              setScoreContext({
                                visit_number: row.visit_number,
                                visitName: row.visit_name,
                                trialId: row.trial_id,
                                trialName: row.trial_name,
                                selectedBlock: row.score_ecrf_name,
                                selectedSessionId: row.id,
                              })
                            }
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              ))}
          </>
        )}
      </Paper>

      {/* SIDE-BY-SIDE COMPARE DIALOG */}
      <CompareDialog
        open={compareOpen}
        images={(patientData || []).flatMap((item) => item.photo || [])}
        onClose={() => setCompareOpen(false)}
        selectedImage={selectedCompareImage}
      />

      {/* ZOOM DIALOG */}
      <ZoomDialog
        open={!!zoomImage}
        onClose={() => {
          setZoomImage(null);
        }}
        image={zoomImage}
      />

      {scoreContext && (
        <EcrfBlocks
          visit_number={scoreContext.visit_number}
          visitName={scoreContext.visitName}
          trialId={scoreContext.trialId}
          trialName={scoreContext.trialName}
          selectedBlock={scoreContext.selectedBlock}
          fromQueryTable={true}
          useCompactModal={true}
          showEcrfBlock={Boolean(scoreContext)}
          setShowEcrfBlock={() => setScoreContext(null)}
          patientId={_id_}
          isTrialLocked={isTrialLocked}
          selectedSessionId={scoreContext.selectedSessionId}
        />
      )}
    </>
  );
};

export default GalleryView;
